import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';

import styles from './fields.module.scss';

import { FormWidgetContext } from '../form';

const FormFieldWidget = function TextInputEditor({ className, style, widgetProps, breakpoint }) {
  const context = React.useContext(FormWidgetContext);

  const [value, setValue] = React.useState('');

  const id = _.get(widgetProps, 'uid');

  const properties = _.get(widgetProps, 'properties', {});

  const label = _.get(widgetProps, 'properties.desktop.label') || "";
  const placeholder = _.get(widgetProps, 'properties.desktop.placeholder') || "";
  const placeholderColor = getPlaceholderColor(properties);
  const field = _.get(widgetProps, 'properties.desktop.field') || "";
  const error = _.get(context.errors, field);

  useEffect(() => {
    context.updateField(id, field, value);
  }, [value]);

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, 'regular')
  }

  var labelStyles = getLabelStyles(properties, breakpoint, 'regular');
  var inputStyles = getInputStyles(properties, breakpoint, 'regular');

  return (
    <div
      id={id}
      className={cx(className, styles.inputField)}
      style={widgetStyles}
      data-widget-id={id}
    >
      {placeholderColor &&
        <style>
          {`
            #${id} input::-webkit-input-placeholder {
              color: ${placeholderColor};
            }
          `}
        </style>
      }
      {label &&
        <label style={labelStyles}>{label}</label>
      }
      <input type='text'
        placeholder={placeholder}
        style={inputStyles}
        value={value}
        onChange={(e) => { setValue(e.target.value) }}
        disabled={false}
      />
      <span className={cx(styles.error, { [styles.active]: error })}>{error}</span>
    </div>
  )
}

/**
 * --------------------------------------------------
 * Field Styles
 * -------------------------------------------------- 
*/
function getFieldStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const activeStyles = _.get(defaultProperties, `${active}`, {});

  //alignment
  const alignment = _.get(deviceProperties, 'styles.layout.alignment') || 'left';
  styles['alignItems'] = alignment;

  if (alignment === 'left') {
    styles['alignItems'] = 'flex-start';
  } else if (alignment === 'right') {
    styles['alignItems'] = 'flex-end';
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Label Styles
 * -------------------------------------------------- 
*/
function getLabelStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const activeStyles = _.get(defaultProperties, `${active}`, {});

  const alignment = _.get(deviceProperties, 'styles.layout.alignment') || 'left';

  const fieldSpacing = _.get(deviceProperties, 'styles.layout.fieldSpacing') || 0;
  styles['paddingBottom'] = `${fieldSpacing}px`;

  const labelPadding = _.get(deviceProperties, 'styles.layout.labelPadding') || 0;
  if (alignment === 'left') {
    styles['paddingLeft'] = `${labelPadding}px`;
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${labelPadding}px`;
  }

  const inputStyles = _.get(defaultProperties, 'styles.regular.label');
  const fontStyles = getFontStyle(inputStyles);

  styles = {
    ...styles,
    ...fontStyles
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Input Styles
 * -------------------------------------------------- 
*/
function getInputStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);

  const alignment = _.get(deviceProperties, 'styles.layout.alignment') || 'left';
  const inputPadding = _.get(deviceProperties, 'styles.layout.inputPadding') || 0;

  if (alignment === 'left') {
    styles['paddingLeft'] = `${inputPadding}px`;
  } else if (alignment === 'right') {
    styles['paddingRight'] = `${inputPadding}px`;
  }

  const inputStyles = _.get(defaultProperties, 'styles.regular.input');
  const fontStyles = getFontStyle(inputStyles);

  styles = {
    ...styles,
    ...fontStyles
  }

  const border = _.get(defaultProperties, 'styles.regular.border');
  const borderStyles = getBorderStyles(border);

  styles = {
    ...styles,
    ...borderStyles
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * -------------------------------------------------- 
*/
export function getPlaceholderColor(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const inputStyles = _.get(defaultProperties, 'styles.regular.input');

  const colorRgb = _.get(inputStyles, 'placeholderColor', null);
  if (!colorRgb) {
    return null;
  }

  return getColorsConfig(colorRgb);
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const borderStyle = _.get(border, 'style') || 'bottom';
  if (borderStyle !== 'none') {
    const borderWidthString = _.get(border, 'width') || 0;
    var borderWidget = parseInt(borderWidthString) + 'px';

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth';
    styles[fieldName] = borderWidget;

    const colorRgb = _.get(border, 'color');
    var color = 'transparent';
    if (colorRgb) {
      color = getColorsConfig(colorRgb);
    }

    styles['borderColor'] = color;
  }

  return styles;
}

export function getFontStyle(fontStyles) {
  var styles = {};

  if (!fontStyles) {
    return styles;
  }

  //background-color
  const backgroundColorRgb = _.get(fontStyles, 'background', null);
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb)
    }
  }
  
  //font-color
  const colorRgb = _.get(fontStyles, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'font.fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: fontFamily
  }

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14;
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + 'px';
    styles['fontSize'] = fontSize;
  }

  return styles;
}

export { FormFieldWidget };
